/* eslint-disable no-useless-escape */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-call */
import { CommonModule } from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  of,
  Subject,
  takeUntil,
} from "rxjs";

import { AccordionModule } from "primeng/accordion";
import { ConfirmationService, MessageService } from "primeng/api";
import {
  AutoCompleteCompleteEvent,
  AutoCompleteModule,
} from "primeng/autocomplete";
import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { CheckboxModule } from "primeng/checkbox";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { DatePickerModule } from "primeng/datepicker";
import { DividerModule } from "primeng/divider";
import { InputNumberModule } from "primeng/inputnumber";
import { InputSwitchModule } from "primeng/inputswitch";
import { InputTextModule } from "primeng/inputtext";
import { PopoverModule } from "primeng/popover";
import { RadioButtonModule } from "primeng/radiobutton";
import { SelectModule } from "primeng/select";
import { SkeletonModule } from "primeng/skeleton";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";

import { CountrySelectComponent } from "../../../../shared/country-select/country-select.component";
import { StateSelectComponent } from "../../../../shared/state-select/state-select.component";

import { QPilotCoupon } from "../../../../core/models/qpilot/coupon.interface";
import {
  QPilotAccessToken,
  QPilotService,
  QPilotUser,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";
import {
  COUPON_CODE_VALIDATION,
  COUPON_MESSAGES,
  COUPON_NAME_VALIDATION,
  DISCOUNT_TYPE_OPTIONS,
  isPercentageDiscount,
  isSubtotalDiscount,
} from "../../constants/coupon.constants";
import {
  AdvancedOptionsForm,
  ConversionLimitsForm,
  CouponDetailsForm,
  CouponForm,
  DiscountConfigForm,
  MAX_ASSIGNMENTS_PER_CUSTOMER_OPTIONS,
  RECURRING_DISCOUNT_CYCLES_OPTIONS,
  RecurringDiscountForm,
} from "../../interfaces/coupon-form.interface";
import {
  CouponsService,
  ProductGroupSearchResult,
} from "../../services/coupons.service";

type PageMode = "create" | "edit";

@Component({
  selector: "app-coupon-edit",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AccordionModule,
    AutoCompleteModule,
    ButtonModule,
    CardModule,
    CheckboxModule,
    ConfirmDialogModule,
    DatePickerModule,
    DividerModule,
    InputNumberModule,
    InputTextModule,
    InputSwitchModule,
    PopoverModule,
    RadioButtonModule,
    SelectModule,
    SkeletonModule,
    ToastModule,
    TooltipModule,
    CountrySelectComponent,
    StateSelectComponent,
  ],
  templateUrl: "./coupon-edit.component.html",
  styleUrl: "./coupon-edit.component.scss",
  providers: [ConfirmationService],
  // Note: MessageService is NOT provided locally - we use the root instance
  // This ensures both component toast calls AND API interceptor error toasts
  // go to the same MessageService that the <p-toast> listens to
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CouponEditComponent implements OnInit, OnDestroy {
  // Input properties for embedded mode
  @Input() inputSiteId?: number;
  @Input() inputToken?: QPilotAccessToken;
  @Input() inputCouponId?: number;
  @Input() inputMode?: PageMode;
  @Input() embeddedMode = false;
  @Input() hideToolbar = false;

  // Services
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef);
  private readonly sessionService = inject(SessionService);
  private readonly qpilotService = inject(QPilotService);
  private readonly couponsService = inject(CouponsService);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly destroy$ = new Subject<void>();

  // Page state
  mode: PageMode = "create";
  couponId: number | null = null;
  siteId: number | null = null;
  loading = signal(true);
  saving = signal(false);
  codeValidating = signal(false);
  codeError = signal<string | null>(null);

  // Retention workflow check - if coupon is in use, disable editing
  // Matches old QMC canEdit property (dashboard-coupons-create-edit.component.ts:65)
  canEdit = signal(true);
  retentionWorkflowMessage = signal<string | null>(null);

  // Subscription tier check - if tier doesn't allow advanced options
  // Matches old QMC advancedOptionsAllowed (dashboard-coupons-create-edit.component.ts:58)
  advancedOptionsAllowed = signal(true);
  currentTierName = signal<string | null>(null);
  upgradeUrl = signal<string | null>(null);

  // Product autocomplete state
  // Matches old QMC productsInAutoComplete (line 67) and selectedProduct (line 68)
  productSuggestions = signal<ProductGroupSearchResult[]>([]);
  // Store display string for autocomplete input (PrimeNG single-mode requires string for proper display)
  selectedProductDisplay: string = "";
  // Store actual product object for form submission
  private selectedProductObj: ProductGroupSearchResult | null = null;

  // Original coupon data (for edit mode)
  originalCoupon: QPilotCoupon | null = null;

  // Form
  couponForm!: FormGroup<CouponForm>;

  // Options for dropdowns (spread to mutable arrays for PrimeNG)
  readonly discountTypeOptions = DISCOUNT_TYPE_OPTIONS;
  readonly maxAssignmentsOptions = [...MAX_ASSIGNMENTS_PER_CUSTOMER_OPTIONS];
  readonly recurringCyclesOptions = [...RECURRING_DISCOUNT_CYCLES_OPTIONS];

  // Weight unit options
  readonly weightUnitOptions = [
    { value: "Pound", label: "Pounds (lb)" },
    { value: "Kilogram", label: "Kilograms (kg)" },
    { value: "Ounce", label: "Ounces (oz)" },
    { value: "Gram", label: "Grams (g)" },
  ];

  // Signal to track discount type changes (for computed reactivity)
  readonly discountTypeSignal = signal<string>("");

  // Computed values for conditional UI (use signal for reactivity)

  // Used for labels: "Percentage" vs "Amount", prefix/suffix display
  // Old QMC shows "Coupon Percentage" for ALL percentage types (lines 37-42)
  // Old QMC shows "Coupon Amount" for ALL amount types (lines 45-51)
  readonly isPercentageDiscountType = computed(() => {
    const discountType = this.discountTypeSignal();
    return isPercentageDiscount(discountType || "");
  });

  // BUG FIX: Old QMC only shows MaxPercentageDiscount for ReduceSubtotalByPercentage
  // NOT for ReduceProductPriceByPercentage (see old QMC line 54)
  readonly showMaxPercentageDiscount = computed(() => {
    const discountType = this.discountTypeSignal();
    return discountType === "ReduceSubtotalByPercentage";
  });

  // BUG FIX: Apply To option only shows for ReduceSubtotalByPercentage
  // Old QMC only shows this for ReduceSubtotalByPercentage, NOT for ReduceSubtotalByAmount
  readonly showApplyToPriceOption = computed(() => {
    const discountType = this.discountTypeSignal();
    return discountType === "ReduceSubtotalByPercentage";
  });

  readonly isProductDiscount = computed(() => {
    const discountType = this.discountTypeSignal();
    return (
      discountType === "ReduceProductPriceByPercentage" ||
      discountType === "ReduceProductPriceByAmount"
    );
  });

  // Show discount limits only for ReduceSubtotalByAmount
  // Matches old QMC dashboard-coupons-create-edit.component.html:308-310
  readonly showDiscountLimits = computed(() => {
    const discountType = this.discountTypeSignal();
    return discountType === "ReduceSubtotalByAmount";
  });

  // Dynamic label based on discount type target (Subtotal, Shipping, Product)
  readonly discountAmountLabel = computed(() => {
    const discountType = this.discountTypeSignal();
    const isPercent = isPercentageDiscount(discountType || "");

    if (discountType?.includes("Subtotal")) {
      return isPercent ? "Subtotal Percentage" : "Subtotal Amount";
    } else if (discountType?.includes("Shipping")) {
      return isPercent ? "Shipping Percentage" : "Shipping Amount";
    } else if (discountType?.includes("Product")) {
      return isPercent ? "Product Percentage" : "Product Amount";
    }
    return isPercent ? "Coupon Percentage" : "Coupon Amount";
  });

  // Dynamic hint based on discount type
  readonly discountAmountHint = computed(() => {
    const discountType = this.discountTypeSignal();
    const isPercent = isPercentageDiscount(discountType || "");

    if (discountType?.includes("Subtotal")) {
      return isPercent ? "% off order subtotal" : "$ off order subtotal";
    } else if (discountType?.includes("Shipping")) {
      return isPercent ? "% off shipping cost" : "$ off shipping cost";
    } else if (discountType?.includes("Product")) {
      return isPercent ? "% off product price" : "$ off product price";
    }
    return isPercent ? "Numeric percentage value" : "Numeric amount value";
  });

  // Check if selected country is USA (for state dropdown vs text input)
  // Using a signal that updates when country changes
  isUSACountry = signal(false);

  // Recurring mode signal for reactive UI updates
  private recurringModeSignal = signal<string>("forever");

  // Max assignments mode signal for reactive UI updates
  private maxAssignmentsModeSignal = signal<string>("Unlimited");

  readonly showCustomCycles = computed(() => {
    return this.recurringModeSignal() === "cycles";
  });

  readonly showCustomMaxPerCustomer = computed(() => {
    return this.maxAssignmentsModeSignal() === "Custom";
  });

  // Page title and button label
  get pageTitle(): string {
    return this.mode === "create" ? "Create Coupon" : "Edit Coupon";
  }

  get submitButtonLabel(): string {
    if (this.saving()) {
      return this.mode === "create" ? "Creating..." : "Saving...";
    }
    return this.mode === "create" ? "Create Coupon" : "Save Changes";
  }

  constructor() {
    this.initForm();

    // Effect to enable/disable advanced option form controls based on subscription tier
    // This is the proper Angular Reactive Forms way to handle disabled state
    effect(() => {
      const allowed = this.advancedOptionsAllowed();
      const advancedControls =
        this.couponForm.controls.advancedOptions.controls;

      // State, City, Postcode controls - enable/disable based on tier
      if (allowed) {
        advancedControls.state.enable({ emitEvent: false });
        advancedControls.city.enable({ emitEvent: false });
        advancedControls.postcode.enable({ emitEvent: false });
      } else {
        advancedControls.state.disable({ emitEvent: false });
        advancedControls.city.disable({ emitEvent: false });
        advancedControls.postcode.disable({ emitEvent: false });
      }
    });
  }

  ngOnInit(): void {
    this.detectEmbeddedMode();
    this.determineMode();
    this.loadSiteId();
    this.setupFormListeners();
  }

  /**
   * Detect embedded mode from HTML attributes (for web component mode)
   * This is CRITICAL for web components to work properly
   */
  private detectEmbeddedMode(): void {
    const element = this.elementRef.nativeElement as HTMLElement;

    // Check HTML attribute for embedded mode
    const embeddedModeAttr =
      element.getAttribute("embedded-mode") ??
      element.getAttribute("embeddedMode");

    if (embeddedModeAttr === "true" || embeddedModeAttr === "") {
      this.embeddedMode = true;
    }

    // Get attributes in embedded mode
    if (this.embeddedMode) {
      // Get siteId from attribute
      const siteIdString =
        this.inputSiteId?.toString() ??
        element.getAttribute("input-site-id") ??
        element.getAttribute("inputSiteId");

      if (siteIdString) {
        this.siteId = parseInt(siteIdString, 10);
      }

      // Get couponId from attribute
      const couponIdString =
        this.inputCouponId?.toString() ??
        element.getAttribute("input-coupon-id") ??
        element.getAttribute("inputCouponId");

      if (couponIdString) {
        this.couponId = parseInt(couponIdString, 10);
      }

      // Get mode from attribute
      const modeString =
        this.inputMode ??
        element.getAttribute("input-mode") ??
        element.getAttribute("inputMode");

      if (modeString) {
        this.mode = modeString as PageMode;
      }

      // Get token from attribute and convert
      const tokenString =
        element.getAttribute("input-token") ??
        element.getAttribute("inputToken");

      // Create inputToken from attribute if:
      // 1. We have a token string from attribute
      // 2. AND inputToken is either missing, not an object, or doesn't have valid TokenBearerAuth
      // Note: Angular Elements may bind the attribute as a raw string, so we check for proper structure
      if (
        tokenString &&
        (!this.inputToken ||
          typeof this.inputToken !== "object" ||
          !this.inputToken.TokenBearerAuth)
      ) {
        this.inputToken = {
          TokenBearerAuth: tokenString,
          TokenAuth: "",
          Id: 0,
          UserId: 0,
          CreatedUtc: new Date(),
          UpdatedUtc: new Date(),
          ExpiresUtc: new Date(),
          User: {} as QPilotUser,
          FirstLogin: 0,
        };
      }
    }
  }

  /**
   * Get access token based on mode (embedded vs standard)
   */
  private getAccessToken(): QPilotAccessToken | undefined {
    if (this.embeddedMode && this.inputToken) {
      return this.inputToken;
    }
    return this.sessionService.accessToken ?? undefined;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize the form with all sections
   */
  private initForm(): void {
    this.couponForm = new FormGroup<CouponForm>({
      details: new FormGroup<CouponDetailsForm>({
        name: new FormControl<string>("", {
          nonNullable: true,
          validators: [Validators.maxLength(COUPON_NAME_VALIDATION.maxLength)],
        }),
        code: new FormControl<string>("", {
          nonNullable: true,
          validators: [
            Validators.required,
            Validators.maxLength(COUPON_CODE_VALIDATION.maxLength),
            Validators.pattern(COUPON_CODE_VALIDATION.pattern),
          ],
        }),
      }),
      discount: new FormGroup<DiscountConfigForm>({
        discountType: new FormControl<string>("ReduceSubtotalByPercentage", {
          nonNullable: true,
          validators: [Validators.required],
        }),
        amount: new FormControl<number | null>(null, {
          validators: [Validators.required, Validators.min(0)],
        }),
        maxPercentageDiscount: new FormControl<number | null>(null),
        subtotalDiscountAppliesToRegularPrice: new FormControl<boolean>(false, {
          nonNullable: true,
        }),
        productId: new FormControl<number | null>(null),
        productGroupId: new FormControl<number | null>(null),
        productDiscountNotAppliesWhenItHasSalePrice: new FormControl<boolean>(
          false,
          { nonNullable: true },
        ),
        discountLimitsMode: new FormControl<"limited" | "unlimited">(
          "unlimited",
          { nonNullable: true },
        ),
      }),
      recurring: new FormGroup<RecurringDiscountForm>({
        recurringMode: new FormControl<"forever" | "cycles" | "once">(
          "forever",
          { nonNullable: true },
        ),
        maxCyclesPerScheduledOrder: new FormControl<number | null>(null),
        isStackable: new FormControl<boolean>(false, { nonNullable: true }),
        expirationDate: new FormControl<Date | null>(null),
        applyExpirationDateToExisting: new FormControl<boolean>(false, {
          nonNullable: true,
        }),
      }),
      conversionLimits: new FormGroup<ConversionLimitsForm>({
        maxAssignmentsPerCustomerMode: new FormControl<string>("Unlimited", {
          nonNullable: true,
        }),
        maxAssignmentsPerCustomer: new FormControl<number | null>(null),
        maxAssignmentsPerSite: new FormControl<number | null>(null),
      }),
      advancedOptions: new FormGroup<AdvancedOptionsForm>({
        minScheduledOrderCycles: new FormControl<number | null>(null),
        maxScheduledOrderCycles: new FormControl<number | null>(null),
        country: new FormControl<string | null>(null),
        state: new FormControl<string | null>(null),
        city: new FormControl<string | null>(null),
        postcode: new FormControl<string | null>(null),
        minUnits: new FormControl<number | null>(null),
        minSubtotal: new FormControl<number | null>(null),
        minWeight: new FormControl<number | null>(null),
        maxWeight: new FormControl<number | null>(null),
        weightUnitType: new FormControl<string | null>(null),
        customerMode: new FormControl<"all" | "include" | "exclude">("all", {
          nonNullable: true,
        }),
        includeCustomerEmails: new FormControl<string | null>(null),
        excludeCustomerEmails: new FormControl<string | null>(null),
      }),
    });
  }

  /**
   * Setup form value change listeners
   */
  private setupFormListeners(): void {
    // Code validation - debounce and validate uniqueness
    this.couponForm.controls.details.controls.code.valueChanges
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((code) => {
        if (code && code.trim().length > 0) {
          this.validateCouponCode(code);
        } else {
          this.codeError.set(null);
        }
      });

    // Discount type changes - update signal for computed reactivity
    this.couponForm.controls.discount.controls.discountType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.discountTypeSignal.set(value || "");
        this.cdr.markForCheck();
      });

    // Initialize discount type signal with current value
    this.discountTypeSignal.set(
      this.couponForm.controls.discount.controls.discountType.value || "",
    );

    // Recurring mode changes - update signal for reactive UI
    this.couponForm.controls.recurring.controls.recurringMode.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => {
        this.recurringModeSignal.set(mode || "forever");
        if (mode === "once") {
          this.couponForm.controls.recurring.controls.maxCyclesPerScheduledOrder.setValue(
            1,
          );
        } else if (mode === "forever") {
          this.couponForm.controls.recurring.controls.maxCyclesPerScheduledOrder.setValue(
            null,
          );
        }
        this.cdr.markForCheck();
      });

    // Initialize recurring mode signal with current value
    this.recurringModeSignal.set(
      this.couponForm.controls.recurring.controls.recurringMode.value ||
        "forever",
    );

    // Max assignments mode changes - update signal for reactive UI
    this.couponForm.controls.conversionLimits.controls.maxAssignmentsPerCustomerMode.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => {
        this.maxAssignmentsModeSignal.set(mode || "Unlimited");
        this.cdr.markForCheck();
      });

    // Initialize max assignments mode signal with current value
    this.maxAssignmentsModeSignal.set(
      this.couponForm.controls.conversionLimits.controls
        .maxAssignmentsPerCustomerMode.value || "Unlimited",
    );

    // Max assignments per customer mode changes
    this.couponForm.controls.conversionLimits.controls.maxAssignmentsPerCustomerMode.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((mode) => {
        if (mode === "Unlimited") {
          this.couponForm.controls.conversionLimits.controls.maxAssignmentsPerCustomer.setValue(
            null,
          );
        } else if (mode !== "Custom") {
          const numValue = parseInt(mode, 10);
          if (!isNaN(numValue)) {
            this.couponForm.controls.conversionLimits.controls.maxAssignmentsPerCustomer.setValue(
              numValue,
            );
          }
        }
        this.cdr.markForCheck();
      });
  }

  /**
   * Determine create vs edit mode from route
   */
  private determineMode(): void {
    const url = this.router.url;
    if (url.includes("/create")) {
      this.mode = "create";
    } else {
      this.mode = "edit";
      const idParam = this.route.snapshot.paramMap.get("id");
      if (idParam) {
        this.couponId = parseInt(idParam, 10);
      }
    }
  }

  /**
   * Load site ID from route
   */
  private loadSiteId(): void {
    // Embedded mode: siteId already set in detectEmbeddedMode
    if (this.embeddedMode && this.inputToken && this.siteId) {
      this.checkSubscriptionTierPermissions();
      this.loadData();
      return;
    }

    // Standard mode: get site ID from route
    if (!this.embeddedMode) {
      this.route.parent?.paramMap
        .pipe(takeUntil(this.destroy$))
        .subscribe((params) => {
          const siteIdParam = params.get("siteId");
          if (siteIdParam) {
            this.siteId = parseInt(siteIdParam, 10);
            this.checkSubscriptionTierPermissions();
            this.loadData();
          }
        });
    }
  }

  /**
   * Check subscription tier permissions for advanced options
   * Matches old QMC getSusbcriptionTierPermissions (dashboard-coupons-create-edit.component.ts:103-108)
   */
  private checkSubscriptionTierPermissions(): void {
    if (!this.siteId) return;

    const accessToken = this.getAccessToken();
    if (!accessToken) return;

    // Get the page URL for permission check
    // Old QMC uses just "coupons" for the permission check, not "coupons/edit" or "coupons/create"
    const pagePath = "coupons";

    this.qpilotService
      .getPageByUrl(pagePath, this.siteId.toString(), accessToken)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null)),
      )
      .subscribe({
        next: (pageData) => {
          if (pageData) {
            this.advancedOptionsAllowed.set(pageData.allowedForSubscription);
          }
          this.cdr.markForCheck();
        },
      });

    // Also fetch the site tier for display
    this.qpilotService
      .fetchSiteSubscriptionTier(accessToken, this.siteId.toString())
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of(null)),
      )
      .subscribe({
        next: (siteTier) => {
          if (siteTier?.TierName) {
            this.currentTierName.set(`${siteTier.TierName} Plan`);
          }
          if (siteTier?.UpgradeUrl) {
            this.upgradeUrl.set(siteTier.UpgradeUrl);
          }
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Load coupon data for edit mode
   */
  private loadData(): void {
    if (this.mode === "edit" && this.couponId) {
      this.loading.set(true);
      const accessToken = this.getAccessToken();
      this.couponsService
        .getCoupon(this.couponId, accessToken)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (coupon) => {
            this.originalCoupon = coupon;
            this.populateForm(coupon);
            this.loading.set(false);
            // Check if coupon is used in retention workflow (matches old QMC)
            this.checkRetentionWorkflow();
            this.cdr.markForCheck();
          },
          error: () => {
            this.messageService.add({
              severity: "error",
              summary: "Error",
              detail: COUPON_MESSAGES.LOAD_SINGLE_ERROR,
            });
            this.loading.set(false);
            this.cdr.markForCheck();
          },
        });
    } else {
      this.loading.set(false);
    }
  }

  /**
   * Populate form with coupon data
   */
  private populateForm(coupon: QPilotCoupon): void {
    // Details section
    this.couponForm.controls.details.patchValue({
      name: coupon.Name || "",
      code: coupon.Code || "",
    });

    // Discount section
    this.couponForm.controls.discount.patchValue({
      discountType: coupon.DiscountType || "ReduceSubtotalByPercentage",
      amount: coupon.Amount,
      maxPercentageDiscount: coupon.MaxPercentageDiscount || null,
      subtotalDiscountAppliesToRegularPrice:
        coupon.SubtotalDiscountAppliesToRegularPrice || false,
      productId: coupon.ProductId || null,
      productGroupId: coupon.ProductGroupId || null,
      productDiscountNotAppliesWhenItHasSalePrice:
        coupon.ProductDiscountNotAppliesWhenItHasSalePrice || false,
      // BUG FIX: Discount limits logic - when MaxDiscountPerCustomer is SET, it means UNLIMITED mode
      // Old QMC: UNLIMITED mode sets MaxDiscountPerCustomer = Amount (credit carries forward)
      // Old QMC: LIMITED mode sets MaxDiscountPerCustomer = null (discount limited to order value)
      discountLimitsMode:
        coupon.MaxDiscountPerCustomer != null ? "unlimited" : "limited",
    });

    // Update discount type signal for computed reactivity
    this.discountTypeSignal.set(
      coupon.DiscountType || "ReduceSubtotalByPercentage",
    );

    // Restore selectedProduct for product discount types
    // Matches old QMC populateForm logic for product autocomplete
    if (coupon.ProductId || coupon.ProductGroupId) {
      // Set initial display with ID while we fetch the name
      const productId = coupon.ProductId || coupon.ProductGroupId || 0;
      const isProduct = !!coupon.ProductId;
      const displayName = isProduct
        ? `Product #${coupon.ProductId}`
        : `Product Group #${coupon.ProductGroupId}`;
      this.selectedProductDisplay = displayName;
      this.selectedProductObj = {
        productAndGroupId: productId,
        externalId: "",
        productAndGroupName: displayName,
        price: 0,
        salePrice: 0,
        type: isProduct ? "Prod" : "Group",
      };

      // Fetch the actual product name from the API
      this.loadProductName(productId, isProduct ? "Prod" : "Group");
    }

    // Recurring section - determine mode from MaxCyclesPerScheduledOrder
    let recurringMode: "forever" | "cycles" | "once" = "forever";
    if (coupon.MaxCyclesPerScheduledOrder === 1) {
      recurringMode = "once";
    } else if (
      coupon.MaxCyclesPerScheduledOrder &&
      coupon.MaxCyclesPerScheduledOrder > 1
    ) {
      recurringMode = "cycles";
    }

    this.couponForm.controls.recurring.patchValue({
      recurringMode,
      maxCyclesPerScheduledOrder: coupon.MaxCyclesPerScheduledOrder || null,
      isStackable: coupon.IsStackable || false,
      expirationDate: coupon.ExpirationDate
        ? new Date(coupon.ExpirationDate)
        : null,
      applyExpirationDateToExisting:
        coupon.ApplyExpirationDateToExisting || false,
    });

    // Update recurring mode signal for reactive UI
    this.recurringModeSignal.set(recurringMode);

    // Conversion limits - determine mode from MaxAssignmentsPerCustomer
    let maxAssignmentsMode = "Unlimited";
    if (coupon.MaxAssignmentsPerCustomer != null) {
      const value = coupon.MaxAssignmentsPerCustomer;
      if (value >= 1 && value <= 5) {
        maxAssignmentsMode = value.toString();
      } else {
        maxAssignmentsMode = "Custom";
      }
    }

    this.couponForm.controls.conversionLimits.patchValue({
      maxAssignmentsPerCustomerMode: maxAssignmentsMode,
      maxAssignmentsPerCustomer: coupon.MaxAssignmentsPerCustomer || null,
      maxAssignmentsPerSite: coupon.MaxAssignmentsPerSite || null,
    });

    // Update max assignments mode signal for reactive UI
    this.maxAssignmentsModeSignal.set(maxAssignmentsMode);

    // Advanced options - determine customer mode
    let customerMode: "all" | "include" | "exclude" = "all";
    if (coupon.IncludeCustomerEmails && coupon.IncludeCustomerEmails.trim()) {
      customerMode = "include";
    } else if (
      coupon.ExcludeCustomerEmails &&
      coupon.ExcludeCustomerEmails.trim()
    ) {
      customerMode = "exclude";
    }

    this.couponForm.controls.advancedOptions.patchValue({
      minScheduledOrderCycles: coupon.MinScheduledOrderCycles || null,
      maxScheduledOrderCycles: coupon.MaxScheduledOrderCycles || null,
      country: coupon.Country || null,
      state: coupon.State || null,
      city: coupon.City || null,
      postcode: coupon.Postcode || null,
      minUnits: coupon.MinUnits || null,
      minSubtotal: coupon.MinSubtotal || null,
      minWeight: coupon.MinWeight || null,
      maxWeight: coupon.MaxWeight || null,
      weightUnitType: coupon.WeightUnitType || null,
      customerMode,
      includeCustomerEmails: coupon.IncludeCustomerEmails || null,
      excludeCustomerEmails: coupon.ExcludeCustomerEmails || null,
    });

    // Set isUSACountry based on loaded coupon country
    const isUSA = coupon.Country === "US" || coupon.Country === "USA";
    this.isUSACountry.set(isUSA);
  }

  /**
   * Validate coupon code uniqueness
   */
  private validateCouponCode(code: string): void {
    if (!this.siteId) return;

    this.codeValidating.set(true);
    const accessToken = this.getAccessToken();
    this.couponsService
      .validateCouponCode(
        this.siteId,
        code,
        this.couponId ?? undefined,
        accessToken,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (error) => {
          this.codeError.set(error);
          this.codeValidating.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.codeValidating.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Navigate back to list
   */
  goBack(): void {
    if (this.embeddedMode) {
      // Emit event for parent to handle navigation with standard pattern
      const event = new CustomEvent("qmc-coupon-saved", {
        detail: {
          component: "coupon-edit",
          action: "list",
          couponId: this.couponId,
          siteId: this.siteId,
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else if (this.siteId) {
      void this.router.navigate(["/widgets/dashboard", this.siteId, "coupons"]);
    }
  }

  /**
   * Submit form
   * Validates all fields including server-side code uniqueness before saving
   * Matches old QMC validateCoupon() pattern (dashboard-coupons-create-edit.component.ts:366-383)
   */
  onSubmit(): void {
    this.couponForm.markAllAsTouched();

    // Run all validations - matches old QMC pendingCouponIsValid()
    if (!this.validateFormBeforeSubmit()) {
      return;
    }

    // Re-validate coupon code uniqueness before submit
    // This ensures the code is still valid even if user changed it after initial validation
    this.validateAndSubmit();
  }

  /**
   * Validate form before submit
   * Matches old QMC pendingCouponIsValid() validation logic
   * @returns true if valid, false if invalid
   */
  private validateFormBeforeSubmit(): boolean {
    const form = this.couponForm.getRawValue();

    // Check if form is invalid (Angular validators)
    if (this.couponForm.invalid) {
      this.messageService.add({
        severity: "warn",
        summary: "Validation Error",
        detail: COUPON_MESSAGES.VALIDATION_ERROR,
      });
      return false;
    }

    // Check code is filled - matches old QMC: "Code must be filled"
    if (!form.details.code || form.details.code.trim().length === 0) {
      this.messageService.add({
        severity: "warn",
        summary: "Validation Error",
        detail: COUPON_MESSAGES.CODE_REQUIRED_ERROR,
      });
      return false;
    }

    // Check existing code error from debounced validation
    if (this.codeError()) {
      this.messageService.add({
        severity: "warn",
        summary: "Validation Error",
        detail: COUPON_MESSAGES.CODE_DUPLICATE_ERROR,
      });
      return false;
    }

    // Check percentage range for percentage types - matches old QMC
    if (isPercentageDiscount(form.discount.discountType)) {
      const amount = form.discount.amount ?? 0;
      if (amount < 0 || amount > 100) {
        this.messageService.add({
          severity: "warn",
          summary: "Validation Error",
          detail: COUPON_MESSAGES.PERCENTAGE_RANGE_ERROR,
        });
        return false;
      }
    }

    // Check product requirement for product discount types
    // Matches old QMC: "Product is a required field"
    if (
      form.discount.discountType === "ReduceProductPriceByPercentage" ||
      form.discount.discountType === "ReduceProductPriceByAmount"
    ) {
      if (!form.discount.productId && !form.discount.productGroupId) {
        this.messageService.add({
          severity: "warn",
          summary: "Validation Error",
          detail: COUPON_MESSAGES.PRODUCT_REQUIRED_ERROR,
        });
        return false;
      }
    }

    return true;
  }

  /**
   * Validate coupon code and submit if valid
   * Matches old QMC validateCoupon() pattern - validates code uniqueness before submit
   */
  private validateAndSubmit(): void {
    if (!this.siteId) return;

    const code = this.couponForm.controls.details.controls.code.value;

    // Show loading state while validating
    this.saving.set(true);

    // Validate coupon code uniqueness before submit
    const accessToken = this.getAccessToken();
    this.couponsService
      .validateCouponCode(
        this.siteId,
        code,
        this.couponId ?? undefined,
        accessToken,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (errorMessage) => {
          if (errorMessage) {
            // Validation failed - show error and stop
            this.codeError.set(errorMessage);
            this.messageService.add({
              severity: "warn",
              summary: "Validation Error",
              detail: errorMessage,
            });
            this.saving.set(false);
            this.cdr.markForCheck();
          } else {
            // Validation passed - proceed with submit
            this.codeError.set(null);
            const couponData = this.buildCouponData();

            if (this.mode === "create") {
              this.createCoupon(couponData);
            } else {
              this.updateCoupon(couponData);
            }
          }
        },
        error: () => {
          // On error, proceed with submit (don't block on validation failure)
          // The server will catch duplicate codes anyway
          const couponData = this.buildCouponData();

          if (this.mode === "create") {
            this.createCoupon(couponData);
          } else {
            this.updateCoupon(couponData);
          }
        },
      });
  }

  /**
   * Build coupon data from form values
   */
  private buildCouponData(): Partial<QPilotCoupon> {
    const form = this.couponForm.getRawValue();

    // Build base coupon data
    const coupon: Partial<QPilotCoupon> = {
      SiteId: this.siteId!,
      Name: form.details.name,
      Code: form.details.code,
      DiscountType: form.discount.discountType,
      Amount: form.discount.amount ?? 0,
      IsStackable: form.recurring.isStackable,
      Active: true,
    };

    // Add max percentage discount if applicable
    if (isPercentageDiscount(form.discount.discountType)) {
      coupon.MaxPercentageDiscount = form.discount.maxPercentageDiscount ?? 0;
    }

    // Add subtotal options
    if (isSubtotalDiscount(form.discount.discountType)) {
      coupon.SubtotalDiscountAppliesToRegularPrice =
        form.discount.subtotalDiscountAppliesToRegularPrice;
    }

    // Add product discount options
    if (
      form.discount.discountType === "ReduceProductPriceByPercentage" ||
      form.discount.discountType === "ReduceProductPriceByAmount"
    ) {
      coupon.ProductId = form.discount.productId ?? undefined;
      coupon.ProductGroupId = form.discount.productGroupId ?? undefined;
      coupon.ProductDiscountNotAppliesWhenItHasSalePrice =
        form.discount.productDiscountNotAppliesWhenItHasSalePrice;
    }

    // Discount limits - BUG FIX: Old QMC sets MaxDiscountPerCustomer when UNLIMITED
    // UNLIMITED = "Do not limit discount (credit will carry forward)" → MaxDiscountPerCustomer = Amount
    // LIMITED = "Limit discount (cannot exceed total order value)" → MaxDiscountPerCustomer = null
    if (
      form.discount.discountType === "ReduceSubtotalByAmount" &&
      form.discount.discountLimitsMode === "unlimited"
    ) {
      coupon.MaxDiscountPerCustomer = form.discount.amount ?? 0;
    }

    // Recurring settings
    if (form.recurring.recurringMode === "once") {
      coupon.MaxCyclesPerScheduledOrder = 1;
    } else if (form.recurring.recurringMode === "cycles") {
      coupon.MaxCyclesPerScheduledOrder =
        form.recurring.maxCyclesPerScheduledOrder ?? undefined;
    }

    // Expiration
    if (form.recurring.expirationDate) {
      coupon.ExpirationDate = form.recurring.expirationDate;
      coupon.ApplyExpirationDateToExisting =
        form.recurring.applyExpirationDateToExisting;
    }

    // Conversion limits
    if (form.conversionLimits.maxAssignmentsPerCustomerMode !== "Unlimited") {
      coupon.MaxAssignmentsPerCustomer =
        form.conversionLimits.maxAssignmentsPerCustomer ?? undefined;
    }
    if (form.conversionLimits.maxAssignmentsPerSite) {
      coupon.MaxAssignmentsPerSite =
        form.conversionLimits.maxAssignmentsPerSite;
    }

    // Advanced options - Cycles
    if (form.advancedOptions.minScheduledOrderCycles) {
      coupon.MinScheduledOrderCycles =
        form.advancedOptions.minScheduledOrderCycles;
    }
    if (form.advancedOptions.maxScheduledOrderCycles) {
      coupon.MaxScheduledOrderCycles =
        form.advancedOptions.maxScheduledOrderCycles;
    }

    // Advanced options - Address
    if (form.advancedOptions.country) {
      coupon.Country = form.advancedOptions.country;
    }
    if (form.advancedOptions.state) {
      coupon.State = form.advancedOptions.state;
    }
    if (form.advancedOptions.city) {
      coupon.City = form.advancedOptions.city;
    }
    if (form.advancedOptions.postcode) {
      coupon.Postcode = form.advancedOptions.postcode;
    }

    // Advanced options - Items
    if (form.advancedOptions.minUnits) {
      coupon.MinUnits = form.advancedOptions.minUnits;
    }
    if (form.advancedOptions.minSubtotal) {
      coupon.MinSubtotal = form.advancedOptions.minSubtotal;
    }
    if (form.advancedOptions.minWeight) {
      coupon.MinWeight = form.advancedOptions.minWeight;
    }
    if (form.advancedOptions.maxWeight) {
      coupon.MaxWeight = form.advancedOptions.maxWeight;
    }
    if (form.advancedOptions.weightUnitType) {
      coupon.WeightUnitType = form.advancedOptions.weightUnitType;
    }

    // Advanced options - Customers
    if (form.advancedOptions.customerMode === "include") {
      coupon.IncludeCustomerEmails =
        form.advancedOptions.includeCustomerEmails ?? "";
      coupon.ExcludeCustomerEmails = "";
    } else if (form.advancedOptions.customerMode === "exclude") {
      coupon.IncludeCustomerEmails = "";
      coupon.ExcludeCustomerEmails =
        form.advancedOptions.excludeCustomerEmails ?? "";
    } else {
      coupon.IncludeCustomerEmails = "";
      coupon.ExcludeCustomerEmails = "";
    }

    return coupon;
  }

  /**
   * Create new coupon
   */
  private createCoupon(couponData: Partial<QPilotCoupon>): void {
    const accessToken = this.getAccessToken();
    this.couponsService
      .createCoupon(couponData, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: COUPON_MESSAGES.CREATE_SUCCESS,
          });
          this.saving.set(false);
          setTimeout(() => this.goBack(), 1000);
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: COUPON_MESSAGES.CREATE_ERROR,
          });
          this.saving.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Update existing coupon
   */
  private updateCoupon(couponData: Partial<QPilotCoupon>): void {
    if (!this.couponId) return;

    const accessToken = this.getAccessToken();
    this.couponsService
      .updateCoupon(this.couponId, couponData, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: COUPON_MESSAGES.UPDATE_SUCCESS,
          });
          this.saving.set(false);
          setTimeout(() => this.goBack(), 1000);
        },
        error: () => {
          this.messageService.add({
            severity: "error",
            summary: "Error",
            detail: COUPON_MESSAGES.UPDATE_ERROR,
          });
          this.saving.set(false);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Check if a field has an error
   */
  hasError(path: string): boolean {
    const control = this.couponForm.get(path);
    return !!(control?.invalid && control?.touched);
  }

  /**
   * Get error message for a field
   */
  getErrorMessage(path: string): string {
    const control = this.couponForm.get(path);
    if (!control?.errors) return "";

    if (control.errors["required"]) return "This field is required";
    if (control.errors["maxlength"]) {
      const maxLengthError = control.errors["maxlength"] as {
        requiredLength: number;
      };
      return `Maximum ${maxLengthError.requiredLength} characters allowed`;
    }
    if (control.errors["pattern"]) return COUPON_CODE_VALIDATION.patternMessage;
    if (control.errors["min"]) return "Value must be 0 or greater";

    return "Invalid value";
  }

  /**
   * Get human-readable discount type label
   */
  getDiscountTypeLabel(discountType: string): string {
    const option = this.discountTypeOptions.find(
      (opt) => opt.value === discountType,
    );
    return option?.label ?? discountType;
  }

  /**
   * Coupon code keypress filter
   * Matches old QMC couponKeyPress (dashboard-coupons-create-edit.component.ts:357-364)
   * Old QMC pattern: /^[a-zA-Z0-9_!@#$%&*+\-_]*$/
   * Allows: a-z, A-Z, 0-9, and special characters: _ ! @ # $ % & * + -
   */
  couponKeyPress(event: KeyboardEvent): boolean {
    // Allow: a-z, A-Z, 0-9, and special chars matching old QMC: _ ! @ # $ % & * + -
    const key = event.key;
    if (/^[a-zA-Z0-9_!@#$%&*+\-]$/.test(key)) {
      return true;
    }
    // Allow control keys (backspace, delete, arrow keys, etc.)
    if (
      event.key === "Backspace" ||
      event.key === "Delete" ||
      event.key === "ArrowLeft" ||
      event.key === "ArrowRight" ||
      event.key === "Tab"
    ) {
      return true;
    }
    event.preventDefault();
    return false;
  }

  /**
   * Load product/group name from API for edit mode
   * Fetches the actual name to display instead of just "Product #123"
   */
  private loadProductName(productId: number, type: "Prod" | "Group"): void {
    if (!this.siteId) return;

    // Search with the product ID to try to find it directly
    // The API searches by name, so we search for the ID as text
    const accessToken = this.getAccessToken();
    this.couponsService
      .searchProductsAndGroups(
        this.siteId,
        productId.toString(),
        1,
        accessToken,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          // Find the matching product by ID and type
          const matchingProduct = results.find(
            (p) => p.productAndGroupId === productId && p.type === type,
          );

          if (matchingProduct && matchingProduct.productAndGroupName) {
            // Update display with the actual product name
            const typePrefix = type === "Prod" ? "[Product]" : "[Group]";
            const name = matchingProduct.productAndGroupName;
            // Don't add prefix if name already contains it
            if (name.startsWith("[Product]") || name.startsWith("[Group]")) {
              this.selectedProductDisplay = name;
            } else {
              this.selectedProductDisplay = `${typePrefix} ${name}`;
            }
            this.selectedProductObj = matchingProduct;
            this.cdr.markForCheck();
          }
          // If not found, keep the original "Product #ID" display
        },
      });
  }

  /**
   * Product/ProductGroup autocomplete search
   * Matches old QMC productSearch (dashboard-coupons-create-edit.component.ts:283-295)
   */
  productSearch(event: AutoCompleteCompleteEvent): void {
    if (!this.siteId) return;

    const query = event.query || "";
    const accessToken = this.getAccessToken();
    this.couponsService
      .searchProductsAndGroups(this.siteId, query, 1, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (results) => {
          this.productSuggestions.set(results);
          this.cdr.markForCheck();
        },
        error: () => {
          this.productSuggestions.set([]);
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Handle product selection from autocomplete
   * Matches old QMC productSelected (dashboard-coupons-create-edit.component.ts:297-312)
   * API responses are now normalized to camelCase by the service
   * Note: PrimeNG AutoCompleteSelectEvent has { originalEvent, value } structure
   */
  productSelected(event: { value: ProductGroupSearchResult }): void {
    const product = event.value;
    if (!product) return;

    // Store the full object and update the display string with type prefix
    this.selectedProductObj = product;
    const name = product.productAndGroupName || "";
    // Don't add prefix if name already contains it
    if (name.startsWith("[Product]") || name.startsWith("[Group]")) {
      this.selectedProductDisplay = name;
    } else {
      const typePrefix = product.type === "Prod" ? "[Product]" : "[Group]";
      this.selectedProductDisplay = `${typePrefix} ${name}`;
    }

    const productId = product.productAndGroupId;
    const productType = product.type;

    // Set productId or productGroupId based on type
    if (productType === "Prod") {
      this.couponForm.controls.discount.patchValue({
        productId: productId,
        productGroupId: null,
      });
    } else {
      this.couponForm.controls.discount.patchValue({
        productId: null,
        productGroupId: productId,
      });
    }
    this.cdr.markForCheck();
  }

  /**
   * Clear product selection
   * Matches old QMC productClear (dashboard-coupons-create-edit.component.ts:314-321)
   */
  productClear(): void {
    this.selectedProductObj = null;
    this.selectedProductDisplay = "";
    this.couponForm.controls.discount.patchValue({
      productId: null,
      productGroupId: null,
    });
    this.cdr.markForCheck();
  }

  /**
   * Get product display name for autocomplete item template
   * API responses are now normalized to camelCase by the service
   */
  getProductDisplayName(product: ProductGroupSearchResult): string {
    return product?.productAndGroupName || "";
  }

  /**
   * Get product price for autocomplete item template
   */
  getProductPrice(product: ProductGroupSearchResult): number {
    return product?.price || 0;
  }

  /**
   * Get product sale price for autocomplete item template
   */
  getProductSalePrice(product: ProductGroupSearchResult): number {
    return product?.salePrice || 0;
  }

  /**
   * Get product type for autocomplete item template
   */
  getProductType(product: ProductGroupSearchResult): string {
    return product?.type || "";
  }

  /**
   * Check if coupon is used in retention workflows
   * Matches old QMC (dashboard-coupons-create-edit.component.ts:123-137)
   * Old QMC filters for `status == QPilotOfferStatusTypes.Accepted` (value 0)
   * and matches `item.itemId == this.coupon.Code` (uses coupon CODE, not ID!)
   * If used, sets canEdit to false, disables form, and shows modal dialog
   */
  private checkRetentionWorkflow(): void {
    if (!this.siteId || !this.originalCoupon?.Code) return;

    const accessToken = this.getAccessToken();
    this.couponsService
      .getSiteOffers(this.siteId, accessToken)
      .pipe(
        takeUntil(this.destroy$),
        catchError(() => of({ offers: [] })),
      )
      .subscribe({
        next: (response) => {
          // Get the coupon CODE (not ID!) - matches old QMC: item.itemId == this.coupon.Code
          const couponCode = this.originalCoupon?.Code;

          // Filter for "Accepted" status (old QMC: QPilotOfferStatusTypes.Accepted = 0)
          // The API may return status as number 0 or string "Accepted"
          const acceptedOffers =
            response.offers?.filter(
              (offer) =>
                offer.status === "Accepted" ||
                offer.status === 0 ||
                offer.status === "0",
            ) ?? [];

          // Check if this coupon's CODE is used in any accepted offer's items
          let isUsedInRetentionWorkflow = false;
          for (const offer of acceptedOffers) {
            for (const item of offer.items ?? []) {
              if (item.itemId === couponCode) {
                isUsedInRetentionWorkflow = true;
                break;
              }
            }
            if (isUsedInRetentionWorkflow) break;
          }

          if (isUsedInRetentionWorkflow) {
            this.canEdit.set(false);
            this.retentionWorkflowMessage.set(
              "This coupon is applied to a retention workflow that has collected data and therefore you are not able to make changes to this coupon",
            );
            // Disable the entire form
            this.couponForm.disable();

            // Show modal dialog matching old QMC dialogHelper.alert()
            this.confirmationService.confirm({
              header:
                "Coupon is applied to a retention workflow that has collected data",
              message:
                "This coupon is applied to a retention workflow that has collected data and therefore you are not able to make changes to this coupon",
              icon: "pi pi-exclamation-triangle",
              acceptLabel: "Ok",
              rejectVisible: false,
              acceptButtonStyleClass: "p-button-primary",
            });
          }
          this.cdr.markForCheck();
        },
      });
  }

  /**
   * Handle country change - update isUSACountry signal and clear state
   * (prevents invalid US state abbreviation for non-US countries)
   */
  onCountryChange(): void {
    const country =
      this.couponForm.controls.advancedOptions.controls.country.value;
    const isUSA = country === "US" || country === "USA";
    this.isUSACountry.set(isUSA);

    // Clear state value when country changes
    this.couponForm.controls.advancedOptions.controls.state.setValue(null);
    this.cdr.markForCheck();
  }

  /**
   * Navigate to subscription upgrade page
   * In embedded mode, emit an event for the parent to handle
   * In standard mode, navigate to /my-account/subscription
   */
  navigateToUpgrade(): void {
    if (this.embeddedMode) {
      // Emit event for parent to handle navigation
      const event = new CustomEvent("qmc-navigate", {
        detail: {
          component: "coupon-edit",
          action: "upgrade",
          target: "/my-account/subscription",
        },
        bubbles: true,
        composed: true,
      });
      this.elementRef.nativeElement.dispatchEvent(event);
    } else {
      void this.router.navigate(["/my-account/subscription"]);
    }
  }
}
